import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
} from '@angular/core';
import { DsIconComponent } from '../icons/ds-icon.component';
import type { DsIconName } from '../icons/ds-icons';

export type DsItemListState = 'default' | 'hover' | 'focus' | 'disabled';

@Component({
  selector: 'ds-item-list',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      [class]="hostClasses"
      [attr.role]="isInteractive ? 'button' : null"
      [attr.tabindex]="isInteractive && !isDisabled ? 0 : null"
      [attr.aria-disabled]="isDisabled ? 'true' : null"
      [attr.aria-label]="isInteractive && ariaLabel ? ariaLabel : null"
      (click)="handleClick($event)"
      (keydown)="handleKeydown($event)"
    >
      <div class="ds-item-list__row">
        @if (showIcon) {
          <span class="ds-item-list__icon" aria-hidden="true">
            @if (icon) {
              <ds-icon [name]="icon" size="var(--ds-item-list-icon-size)" />
            } @else {
              <ng-content select="[itemListIcon]" />
            }
          </span>
        }

        <div class="ds-item-list__content">
          <ng-content />
        </div>
      </div>

      @if (showDivider) {
        <span class="ds-item-list__divider" aria-hidden="true"></span>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    .ds-item-list {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
      padding-inline: var(--ds-item-list-padding-x);
      border: 0 solid transparent;
      box-sizing: border-box;
      position: relative;
      background: var(--ds-item-list-background);
      color: var(--ds-item-list-content-color);
      outline: none;
      box-shadow: none;
      transition:
        background-color var(--ds-item-list-transition-duration) ease,
        border-color var(--ds-item-list-transition-duration) ease,
        box-shadow var(--ds-item-list-transition-duration) ease,
        opacity var(--ds-item-list-transition-duration) ease;
    }

    .ds-item-list--rounded {
      overflow: hidden;
      border-radius: var(--ds-item-list-border-radius);
    }

    .ds-item-list--square {
      border-radius: 0;
    }

    .ds-item-list__row {
      display: flex;
      align-items: flex-start;
      gap: var(--ds-item-list-gap);
      width: 100%;
      min-width: 0;
      padding-block: var(--ds-item-list-padding-y);
      box-sizing: border-box;
    }

    .ds-item-list--interactive {
      cursor: pointer;
    }

    .ds-item-list--interactive:hover:not(.ds-item-list--disabled),
    .ds-item-list--hover:not(.ds-item-list--disabled) {
      z-index: 1;
      background: var(--ds-item-list-hover-background);
      box-shadow: 0 0 0 var(--ds-item-list-hover-border-width) var(--ds-item-list-hover-border-color);
    }

    .ds-item-list--interactive:focus-visible:not(.ds-item-list--disabled),
    .ds-item-list--focus:not(.ds-item-list--disabled) {
      border-width: var(--ds-focus-stroke);
      border-color: var(--ds-focus-color);
      box-shadow: none;
    }

    .ds-item-list--interactive:hover:not(.ds-item-list--disabled) .ds-item-list__divider,
    .ds-item-list--interactive:focus-visible:not(.ds-item-list--disabled) .ds-item-list__divider,
    .ds-item-list--hover:not(.ds-item-list--disabled) .ds-item-list__divider,
    .ds-item-list--focus:not(.ds-item-list--disabled) .ds-item-list__divider {
      visibility: hidden;
    }

    .ds-item-list--disabled {
      cursor: not-allowed;
      opacity: calc(var(--ds-item-list-disabled-opacity) / 100px);
      pointer-events: none;
    }

    .ds-item-list__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--ds-item-list-icon-size);
      height: var(--ds-item-list-icon-size);
      flex: 0 0 var(--ds-item-list-icon-size);
      overflow: hidden;
      color: var(--ds-item-list-icon-color);
    }

    .ds-item-list__content {
      display: flex;
      flex: 1 1 0;
      align-items: center;
      min-width: 0;
      min-height: var(--ds-item-list-content-min-height);
    }

    .ds-item-list__divider {
      display: block;
      width: 100%;
      height: var(--ds-item-list-divider-width);
      flex: 0 0 var(--ds-item-list-divider-width);
      background: var(--ds-item-list-divider-color);
    }
  `],
})
export class DsItemListComponent {
  @Input() state: DsItemListState = 'default';
  @Input({ transform: booleanAttribute }) showIcon = true;
  @Input() icon: DsIconName | string = 'Add';
  @Input({ transform: booleanAttribute }) borderBottom = true;
  @Input({ transform: booleanAttribute }) borderRadius = true;
  @Input() interactive: boolean | string | null = null;
  @Input() ariaLabel = '';

  @Output() clicked = new EventEmitter<MouseEvent | KeyboardEvent>();

  get isDisabled(): boolean {
    return this.state === 'disabled';
  }

  get isInteractive(): boolean {
    if (this.interactive === null || this.interactive === undefined) {
      return this.clicked.observers.length > 0;
    }

    return this.interactive === true || this.interactive === '' || this.interactive === 'true';
  }

  get showDivider(): boolean {
    return this.borderBottom;
  }

  get hostClasses(): string {
    return [
      'ds-item-list',
      `ds-item-list--${this.state}`,
      this.borderRadius ? 'ds-item-list--rounded' : 'ds-item-list--square',
      this.isInteractive ? 'ds-item-list--interactive' : '',
    ].filter(Boolean).join(' ');
  }

  handleClick(event: MouseEvent): void {
    if (!this.isInteractive || this.isDisabled) return;
    this.clicked.emit(event);
  }

  handleKeydown(event: KeyboardEvent): void {
    if (!this.isInteractive || this.isDisabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.clicked.emit(event);
  }
}
